import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isAdminSessionValid, loginAdmin, setAdminSession } from "../lib/api";
import "./admin.css";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isAdminSessionValid()) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password.trim()) {
      setError("Please enter your email and password.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await loginAdmin(email.trim(), password);
      setAdminSession({ token: result.token, expiresAt: result.expiresAt });
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(err.message || "Invalid email or password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="al-shell">
      <div className="al-left">
        <div className="al-left-inner">
          <div className="al-brand">
            <span className="al-brand-icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path
                  d="M14 2L4 6.5v7C4 19.1 8.4 24.3 14 26c5.6-1.7 10-6.9 10-12.5v-7L14 2z"
                  fill="rgba(255,255,255,0.18)"
                  stroke="rgba(255,255,255,0.55)"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 14l2.5 2.5L18 11"
                  stroke="#fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="al-brand-name">Shristi School Admin</span>
          </div>

          <div className="al-left-content">
            <h2 className="al-left-heading">
              Manage your school,
              <br />
              effortlessly.
            </h2>
            <p className="al-left-sub">
              One dashboard to run content, announcements, and staff records — all in one place.
            </p>

            <ul className="al-features">
              {[
                {
                  icon: (
                    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
                      <rect x="4" y="2" width="12" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
                      <path d="M7 6h6M7 9h6M7 12h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                      <path d="M8 2.5V1.5a1 1 0 012 0v1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                    </svg>
                  ),
                  text: "Edit pages & announcements",
                },
                {
                  icon: (
                    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
                      <circle cx="7.5" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.3" />
                      <path d="M2 17c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                      <circle cx="14" cy="6.5" r="2" stroke="currentColor" strokeWidth="1.3" />
                      <path d="M16 12c1.5.5 2.5 1.8 2.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                    </svg>
                  ),
                  text: "Manage staff & student records",
                },
                {
                  icon: (
                    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
                      <rect x="3" y="10" width="3" height="7" rx="0.75" stroke="currentColor" strokeWidth="1.3" />
                      <rect x="8.5" y="6" width="3" height="11" rx="0.75" stroke="currentColor" strokeWidth="1.3" />
                      <rect x="14" y="3" width="3" height="14" rx="0.75" stroke="currentColor" strokeWidth="1.3" />
                    </svg>
                  ),
                  text: "View analytics & reports",
                },
              ].map(({ icon, text }) => (
                <li key={text} className="al-feature-item">
                  <span className="al-feature-icon">{icon}</span>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="al-left-footer">© {new Date().getFullYear()} SchoolAdmin · All rights reserved</p>
        </div>
      </div>

      <div className="al-right">
        <div className="al-form-wrap">
          <div className="al-form-header">
            <h1 className="al-title">Welcome back</h1>
            <p className="al-subtitle">Sign in to your admin account</p>
          </div>

          <form className="al-form" onSubmit={onSubmit} noValidate>
            <div className="al-field">
              <label className="al-label" htmlFor="admin-email">
                Email address
              </label>
              <div className="al-input-wrap">
                <svg className="al-input-icon" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M2.5 5.5h15v10a1 1 0 01-1 1h-13a1 1 0 01-1-1v-10z"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinejoin="round"
                  />
                  <path d="M2.5 5.5l7.5 6 7.5-6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
                <input
                  id="admin-email"
                  className="al-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@school.com"
                  autoComplete="username" 
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="al-field">
              <div className="al-label-row">
                <label className="al-label" htmlFor="admin-pass">
                  Password
                </label>
              </div>
              <div className="al-input-wrap">
                <svg className="al-input-icon" viewBox="0 0 20 20" fill="none">
                  <rect x="3.5" y="8.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
                  <path
                    d="M7 8.5V6a3 3 0 016 0v2.5"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                  <circle cx="10" cy="13" r="1.25" fill="currentColor" />
                </svg>
                <input
                  id="admin-pass"
                  className="al-input"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="al-eye"
                  onClick={() => setShowPass((v) => !v)}
                  aria-label={showPass ? "Hide password" : "Show password"}
                >
                  {showPass ? (
                    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
                      <path
                        d="M3 3l14 14M8.5 8.6A3 3 0 0011.4 11.5"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinecap="round"
                      />
                      <path
                        d="M6.1 6.2C4.6 7.2 3.3 8.5 2 10c1.8 2.6 4.6 5 8 5 1.5 0 2.9-.5 4.1-1.2"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinecap="round"
                      />
                      <path
                        d="M10 5c3.4 0 6.2 2.4 8 5-.5.8-1.2 1.6-2 2.3"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
                      <ellipse cx="10" cy="10" rx="8" ry="5" stroke="currentColor" strokeWidth="1.3" />
                      <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="al-error" role="alert">
                <svg viewBox="0 0 20 20" fill="none" width="16" height="16" style={{ flexShrink: 0 }}>
                  <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.4" />
                  <path d="M10 6v5M10 13.5v.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
                {error}
              </div>
            )}

            <button className="al-btn" type="submit" disabled={submitting}>
              {submitting ? "Signing in…" : "Sign in"}
              <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
                <path
                  d="M4 10h12M11 5l5 5-5 5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}